/**
 * OverageAddConfirmDialog (Story 39.4)
 *
 * Shown when a flagged paid tenant adds a client that crosses into per-client
 * overage. Fetches the EXACT itemized quote from the server (Stripe's own line
 * items — never recomputed client-side, F3) and, on confirm, creates the client
 * + charges exactly that (server pins proration_date, F4). Follows the centered
 * form-Dialog scaffold (UI design system §4).
 */
import { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@provenleap/ui";
import { apiPost } from "../../lib/apiClient";

export interface OverageClientDraft {
  name: string;
  contactName?: string;
  contactEmail?: string;
  industry?: string;
  businessGoals?: string;
  notes?: string;
}

export interface OverageRestoreClient {
  id: string;
  name: string;
}

interface PreviewLine {
  description: string | null;
  amount: number; // cents
}
interface PreviewResponse {
  quoteId: string;
  quantity: number;
  lines: PreviewLine[];
  prorationSubtotal: number;
  total: number;
  currency: string;
  billingInterval: "monthly" | "annual";
}

interface CommonProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmed: () => void;
}

type Props = CommonProps &
  (
    | {
        mode?: "add";
        draft: OverageClientDraft;
        restoreClient?: never;
      }
    | {
        mode: "restore";
        restoreClient: OverageRestoreClient | null;
        draft?: never;
      }
  );

interface BoundPreview {
  identity: string;
  data: PreviewResponse;
}

interface ConfirmResponse {
  quoteId: string;
  action: "create" | "restore";
  client: {
    id: string;
    archivedAt: string | null;
  };
}

function normalizedCurrency(currency: string): string {
  return currency.trim().toUpperCase();
}

function isDisplayableCurrency(currency: string): boolean {
  const code = normalizedCurrency(currency);
  if (!/^[A-Z]{3}$/.test(code)) return false;
  try {
    new Intl.NumberFormat("en-US", { style: "currency", currency: code });
    return true;
  } catch {
    return false;
  }
}

function money(minorUnits: number, currency: string): string {
  const code = normalizedCurrency(currency);
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code,
    currencyDisplay: "narrowSymbol",
  });
  const fractionDigits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
  return `${formatter.format(minorUnits / 10 ** fractionDigits)} ${code}`;
}

function isCompletePreview(
  data: Partial<PreviewResponse>,
): data is PreviewResponse {
  if (
    !Number.isSafeInteger(data.quantity) ||
    (data.quantity ?? 0) < 1 ||
    !Number.isSafeInteger(data.prorationSubtotal) ||
    !Number.isSafeInteger(data.total) ||
    typeof data.currency !== "string" ||
    !isDisplayableCurrency(data.currency) ||
    (data.billingInterval !== "monthly" && data.billingInterval !== "annual") ||
    !Array.isArray(data.lines) ||
    data.lines.length === 0
  ) {
    return false;
  }

  let lineTotal = 0;
  for (const line of data.lines) {
    if (
      !line ||
      (line.description !== null && typeof line.description !== "string") ||
      !Number.isSafeInteger(line.amount)
    ) {
      return false;
    }
    lineTotal += line.amount;
    if (!Number.isSafeInteger(lineTotal)) return false;
  }

  return (
    lineTotal === data.prorationSubtotal &&
    data.total === data.prorationSubtotal
  );
}

export function OverageAddConfirmDialog(props: Props) {
  const { open, onOpenChange, onConfirmed } = props;
  const isRestore = props.mode === "restore";
  const restoreClientId = isRestore ? props.restoreClient?.id : undefined;
  const clientName = isRestore
    ? (props.restoreClient?.name ?? "this client")
    : props.draft.name;
  const previewIdentity = isRestore
    ? `restore:${restoreClientId ?? "missing"}`
    : `add:${JSON.stringify([
        props.draft.name,
        props.draft.contactName ?? null,
        props.draft.contactEmail ?? null,
        props.draft.industry ?? null,
        props.draft.businessGoals ?? null,
        props.draft.notes ?? null,
      ])}`;
  const [boundPreview, setBoundPreview] = useState<BoundPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preview =
    boundPreview?.identity === previewIdentity ? boundPreview.data : null;

  // Fetch the exact quote from the server when the dialog opens.
  useEffect(() => {
    if (!open) {
      setBoundPreview(null);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    const requestIdentity = previewIdentity;
    setBoundPreview(null);
    setLoading(true);
    setError(null);
    const previewBody = isRestore ? { restoreClientId } : {};
    if (isRestore && !restoreClientId) {
      setLoading(false);
      setError("Choose an archived client before requesting a billing quote");
      return;
    }
    apiPost("/api/billing/preview-add-client", previewBody)
      .then(async (res) => {
        if (!res.ok)
          throw new Error((await res.json())?.detail ?? "Preview failed");
        return res.json();
      })
      .then((body) => {
        if (cancelled) return;
        const data = body.data as Partial<PreviewResponse> | undefined;
        if (
          !data ||
          typeof data.quoteId !== "string" ||
          data.quoteId.trim() === ""
        ) {
          throw new Error("Preview response is missing a quote ID");
        }
        if (!isCompletePreview(data)) {
          throw new Error("Preview response is incomplete or inconsistent");
        }
        setBoundPreview({ identity: requestIdentity, data });
      })
      .catch((e) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Preview failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isRestore, open, previewIdentity, restoreClientId]);

  function handleOpenChange(next: boolean): void {
    if (!next && submitting) return; // never lose an in-flight charge
    onOpenChange(next);
  }

  async function confirm(): Promise<void> {
    if (!preview) return;
    const confirmedPreview = preview;
    const expectedRestoreClientId = restoreClientId;
    setSubmitting(true);
    setError(null);
    try {
      const confirmBody = isRestore
        ? { quoteId: confirmedPreview.quoteId }
        : { ...props.draft, quoteId: confirmedPreview.quoteId };
      const res = await apiPost("/api/billing/confirm-add-client", confirmBody);
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(
          body?.detail ??
            (isRestore ? "Failed to restore client" : "Failed to add client"),
        );
      }
      const data = body?.data as Partial<ConfirmResponse> | undefined;
      const expectedAction = isRestore ? "restore" : "create";
      if (
        !data ||
        data.quoteId !== confirmedPreview.quoteId ||
        data.action !== expectedAction ||
        typeof data.client?.id !== "string" ||
        data.client.id.trim() === "" ||
        data.client.archivedAt !== null ||
        (isRestore && data.client.id !== expectedRestoreClientId)
      ) {
        throw new Error("Confirmation response is incomplete or inconsistent");
      }
      onConfirmed();
      onOpenChange(false);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : isRestore
            ? "Failed to restore client"
            : "Failed to add client",
      );
    } finally {
      setSubmitting(false);
    }
  }

  // The overage delta is the sum of the proration (non-zero) lines.
  const dueLabel =
    preview?.billingInterval === "annual"
      ? "charged now for the rest of your term"
      : "added to your next bill";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="flex flex-col space-y-1.5 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    {isRestore
                      ? "Restore a client beyond your plan"
                      : "Add a client beyond your plan"}
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {isRestore ? "Restoring" : "Adding"}{" "}
                    <span className="font-medium">{clientName}</span>{" "}
                    {isRestore ? "brings you" : "goes"} past your included
                    clients — here&apos;s exactly what it costs.
                  </DialogPrimitive.Description>
                </div>

                <div className="mt-6 space-y-3" data-testid="overage-preview">
                  {loading && (
                    <p className="text-sm text-muted-foreground">
                      Calculating…
                    </p>
                  )}
                  {error && (
                    <p className="text-[13px] text-destructive" role="alert">
                      {error}
                    </p>
                  )}
                  {preview && !loading && (
                    <>
                      {preview.lines
                        .filter((l) => l.amount !== 0)
                        .map((l, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between text-[13px]"
                          >
                            <span className="text-muted-foreground">
                              {l.description ?? "Overage"}
                            </span>
                            <span className="font-medium text-foreground">
                              {money(l.amount, preview.currency)}
                            </span>
                          </div>
                        ))}
                      <div className="flex items-center justify-between border-t-[0.5px] border-input/70 pt-3 text-[13px]">
                        <span className="font-medium">Overage {dueLabel}</span>
                        <span className="font-semibold text-foreground">
                          {money(preview.prorationSubtotal, preview.currency)}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <Button
                    variant="outline"
                    className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: "0.5px solid hsl(214 25% 70%)" }}
                    onClick={() => handleOpenChange(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="brand"
                    className="rounded-2xl text-[13px] cursor-pointer"
                    onClick={confirm}
                    disabled={loading || submitting || !preview}
                  >
                    {submitting
                      ? isRestore
                        ? "Restoring…"
                        : "Adding…"
                      : isRestore
                        ? "Restore client & confirm"
                        : "Add client & confirm"}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
